/* eslint-disable react/require-default-props */
/* eslint-disable react/react-in-jsx-scope */
import { useMemo, useState, useEffect, useRef } from 'react';
import { Cross } from '@saleshandy/icons';
import Select from '../../../../shared/design-system/components/select';
import { FilterComponentType } from '../../types';
import DropDownFilterComponent from './v2-dropdown-filter';

interface GroupOption {
  key: string;
  label: string;
  paramKey: string;
}

interface GroupDropdownFilterProps {
  subFilter: {
    groupOptions: GroupOption[];
    rangeList: any[];
    key?: string;
    placeholder?: string;
  };
  handleQuery: (params: {
    filterKey: string;
    filterValue: any;
    filterType: string;
  }) => void;
  pendingFilters: URLSearchParams;
  applyPendingFilters?: (filters?: URLSearchParams) => void;
}

const GroupDropdownFilter = ({
  subFilter,
  handleQuery,
  pendingFilters,
  applyPendingFilters,
}: GroupDropdownFilterProps) => {
  const { groupOptions = [], rangeList = [] } = subFilter;

  // Track which groups have been added (separate from their actual range values)
  const [addedGroupKeys, setAddedGroupKeys] = useState<string[]>([]);
  const prevKeysWithValuesRef = useRef<Set<string>>(new Set());

  // Sync addedGroupKeys from pendingFilters: clear on clear-all / individual tag removal,
  // but preserve empty rows (added but no range selected yet)
  useEffect(() => {
    const keysFromPending: string[] = [];
    groupOptions.forEach((option) => {
      const paramValue = pendingFilters.get(option.paramKey);
      if (paramValue && paramValue !== '' && paramValue !== 'select') {
        keysFromPending.push(option.paramKey);
      }
    });
    const keysFromPendingSet = new Set(keysFromPending);
    const prevKeysWithValues = prevKeysWithValuesRef.current;

    setAddedGroupKeys((prev) => {
      const removedKeys = Array.from(prevKeysWithValues).filter(
        (k) => !keysFromPendingSet.has(k),
      );
      const newKeys = prev.filter((k) => !removedKeys.includes(k));
      const merged = new Set([...newKeys, ...keysFromPending]);
      return Array.from(merged);
    });

    prevKeysWithValuesRef.current = keysFromPendingSet;
  }, [pendingFilters.toString(), groupOptions]);

  // Determine which groups are selected (union of addedGroupKeys + keys with values in URL)
  const selectedGroups = useMemo(() => {
    const selectedParamKeys = new Set(addedGroupKeys);
    
    // Also include groups that have actual values in pendingFilters
    groupOptions.forEach((option) => {
      const paramValue = pendingFilters.get(option.paramKey);
      if (paramValue && paramValue !== '' && paramValue !== 'select') {
        selectedParamKeys.add(option.paramKey);
      }
    });

    return groupOptions.filter((opt) => selectedParamKeys.has(opt.paramKey));
  }, [groupOptions, addedGroupKeys, pendingFilters]);

  // Available options = all options minus already selected ones
  const availableOptions = useMemo(() => {
    const selectedParamKeys = new Set(selectedGroups.map((g) => g.paramKey));
    return groupOptions.filter((opt) => !selectedParamKeys.has(opt.paramKey));
  }, [groupOptions, selectedGroups]);

  // Handle selecting a new group from the top dropdown
  const handleGroupSelect = (selectedKey: string) => {
    if (!selectedKey || selectedKey === 'select') {
      return;
    }
    const matchedOption = groupOptions.find((opt) => opt.key === selectedKey);
    if (!matchedOption) return;
    
    // Add to local state to show the row
    setAddedGroupKeys((prev) => {
      if (prev.includes(matchedOption.paramKey)) return prev;
      return [...prev, matchedOption.paramKey];
    });
  };

  // Handle removing a group row
  const handleRemoveGroup = (option: GroupOption) => {
    // Remove from local state
    setAddedGroupKeys((prev) => prev.filter((key) => key !== option.paramKey));
    
    // Clear the URL param (if user had selected a range)
    handleQuery({
      filterKey: option.paramKey,
      filterValue: '',
      filterType: FilterComponentType.DROP_DOWN,
    });
  };

  // Prepare options for the Select component
  const selectOptions = useMemo(() => {
    const placeholder = { 
      key: 'select', 
      label: subFilter.placeholder || 'Select Level'
    };
    if (availableOptions.length === 0) {
      return [{ key: 'select', label: 'All levels selected' }];
    }
    return [placeholder, ...availableOptions];
  }, [availableOptions, subFilter.placeholder]);

  return (
    <div className="group-dropdown-filter">
      {/* Top selector dropdown */}
      <div className="group-selector">
        <Select
          options={selectOptions}
          selectedOptionKey="select"
          optionRenderer={(option: any) => <span>{option.label}</span>}
          onChange={([option]: any) => {
            if (option.key !== 'select') {
              handleGroupSelect(option.key);
            }
          }}
          selectedOptionRenderer={([option]: any) => (
            <span>{option.label}</span>
          )}
          className="dropdown-filter-select"
          disabled={availableOptions.length === 0}
        />
      </div>

      {/* Render a row for each selected group */}
      {selectedGroups.map((group) => (
        <div key={group.paramKey} className="group-dropdown-row">
          <div className="group-row-header">
            <span className="group-row-label">{group.label}</span>
            <button
              type="button"
              className="group-row-remove"
              onClick={() => handleRemoveGroup(group)}
              aria-label={`Remove ${group.label}`}
            >
              <Cross width={16} height={16} />
            </button>
          </div>
          <div className="group-row-dropdown">
            <DropDownFilterComponent
              subFilter={{
                key: group.paramKey,
                list: rangeList,
                name: '',
              }}
              handleQuery={handleQuery}
              isDisabledByUnknown={false}
              pendingFilters={pendingFilters}
              applyPendingFilters={applyPendingFilters}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default GroupDropdownFilter;
